/**
 * Merge a Listbox selection write-back into the model without dropping
 * model values that have no matching option (e.g. options filtered out by
 * upstream constraints). Known values are replaced by the new selection;
 * unknown values are preserved in their original position.
 */
export function mergeSelectionPreservingUnknown<T extends string | number>(
  currentModel: T[],
  optionValues: Set<T>,
  newlySelectedValues: T[]
): T[] {
  const preserved = currentModel.filter((value) => !optionValues.has(value));
  const preservedSet = new Set(preserved);
  return [
    ...preserved,
    ...newlySelectedValues.filter((value) => !preservedSet.has(value)),
  ];
}

/**
 * Order options with snapshot-selected ones first (each group alphabetical
 * by label). The snapshot is taken when the dropdown opens so options do
 * not reorder while the user is toggling them.
 */
export function sortSelectedFirst<
  V extends string | number,
  O extends { label: string; value: V },
>(options: O[], selectedSnapshot: Set<V>): O[] {
  return [...options].sort((a, b) => {
    const aSelected = selectedSnapshot.has(a.value) ? 0 : 1;
    const bSelected = selectedSnapshot.has(b.value) ? 0 : 1;
    if (aSelected !== bSelected) return aSelected - bSelected;
    return a.label.localeCompare(b.label);
  });
}
